<div class="container mat-typography">
  <header class="hero">
    <div class="hero__who">
      <div class="hero__avatar" [class.hero__avatar--blocked]="user.blocked">
        <app-user-image
          [imgSrc]="user.profileImageUrl"
          [height]="76"
          [width]="76"
          [isAvatar]="true"
          [userId]="user.id"
          (avatarClick)="onAvatarClick($event)"
        />
      </div>
      <div class="hero__text">
        <div class="hero__name-row">
          <h2 class="hero__name">{{ user.firstName }} {{ user.lastName }}</h2>
          <span class="blocked-badge" *ngIf="user.blocked">Blocked</span>
        </div>
        <p class="hero__work" *ngIf="user.role || user.companyName">
          {{ user.role }}<span *ngIf="user.role && user.companyName"> at </span
          ><b *ngIf="user.companyName">{{ user.companyName }}</b>
        </p>
        <div class="hero__facts">
          <span class="fact" *ngIf="user.basedIn">
            <mat-icon aria-hidden="true">place</mat-icon>{{ user.basedIn }}
          </span>
          <span class="fact" *ngIf="user.lastSeen">
            <mat-icon aria-hidden="true">schedule</mat-icon>Seen {{ getFromNow(user.lastSeen) }}
          </span>
          <span class="fact" *ngIf="user.createdAt">
            <mat-icon aria-hidden="true">event</mat-icon>Joined {{ getFromNow(user.createdAt) }}
          </span>
        </div>
        <div class="hero__links">
          <!-- The dialog answers "who is this?"; the case page answers what they
               have been doing. Everything on the boards opens this, so the way
               through to the rest of the account has to start here. -->
          <a class="case-link" [routerLink]="['/admin/abuse', user.id]" mat-dialog-close>
            Open full case →
          </a>
          <a class="ext-link" *ngIf="user.linkedinUrl" [href]="user.linkedinUrl" target="_blank" rel="noopener">
            LinkedIn <mat-icon aria-hidden="true">open_in_new</mat-icon>
          </a>
          <a class="ext-link" *ngIf="user.companyUrl" [href]="user.companyUrl" target="_blank" rel="noopener">
            Company site <mat-icon aria-hidden="true">open_in_new</mat-icon>
          </a>
          <button type="button" class="id-chip" (click)="copyId()" [title]="'Copy ' + user.id">
            {{ idCopied ? 'Copied' : 'ID ' + user.id.slice(0, 8) + '…' }}
            <mat-icon aria-hidden="true">{{ idCopied ? 'check' : 'content_copy' }}</mat-icon>
          </button>
        </div>
      </div>
    </div>
    <button mat-icon-button mat-dialog-close aria-label="Close" class="close-button">
      <mat-icon>close</mat-icon>
    </button>
    <div class="account-actions">
      <button
        mat-flat-button
        color="primary"
        class="team-message-button"
        *ngIf="!composerOpen"
        (click)="openComposer()"
      >
        <mat-icon>chat_bubble_outline</mat-icon>
        Message member
      </button>
      <button
        mat-stroked-button
        class="sharon-button"
        *ngIf="!sharonOpen"
        (click)="sharonOpen = true"
      >
        Sharon
      </button>
      <button
        mat-stroked-button
        class="block-button"
        *ngIf="user.blocked === false"
        (click)="block()"
        [disabled]="busy"
      >
        {{ busy === 'block' ? 'Working…' : 'Block account' }}
      </button>
      <button
        mat-stroked-button
        class="unblock-button"
        *ngIf="user.blocked === true"
        (click)="unblock()"
        [disabled]="busy"
      >
        {{ busy === 'unblock' ? 'Working…' : 'Unblock account' }}
      </button>
    </div>
  </header>
  <p class="action-line error" *ngIf="actionError">{{ actionError }}</p>
  <p class="action-line notice" *ngIf="actionNotice">{{ actionNotice }}</p>

  <!-- Writing to a member as the team, or as another account on the senders
       list. Closed until asked for: it sends a real message to a real person
       and there is no way to take it back. -->
  <div class="composer" *ngIf="composerOpen">
    <div class="composer-head">
      <strong>Message {{ user.firstName }}</strong>
      <button mat-icon-button (click)="composerOpen = false" title="Close">
        <mat-icon>expand_less</mat-icon>
      </button>
    </div>

    <mat-form-field appearance="outline" class="sender-field" *ngIf="senders.length">
      <mat-label>Send as</mat-label>
      <mat-select [value]="senderUid" (valueChange)="chooseSender($event)" [disabled]="sending">
        <mat-option *ngFor="let s of senders" [value]="s.uid">{{ s.label }}</mat-option>
      </mat-select>
    </mat-form-field>

    <p class="composer-meta" *ngIf="teamChat as chat">
      <ng-container *ngIf="chat.exists; else noChat">
        Conversation exists &middot; {{ chat.messageCount }} messages &middot; last
        {{ chat.lastMessageAt | date: 'd MMM, HH:mm' }} &middot;
        <span [class.good]="chat.fromMember > 0">
          {{ chat.fromMember > 0 ? 'they have replied' : 'they have never replied' }}
        </span>
        This goes into that thread.
      </ng-container>
      <ng-template #noChat>No conversation yet. This opens one.</ng-template>
    </p>
    <p class="composer-meta bad" *ngIf="teamChat?.blockedByMember">
      This member has blocked {{ sender?.label ?? 'this account' }}. The message will not reach them.
    </p>
    <p class="composer-meta" *ngIf="teamChat?.contacts?.length as n">
      Contacted {{ n }} {{ n === 1 ? 'time' : 'times' }} before, last by
      {{ teamChat?.contacts?.[0]?.sentBy
      }}<ng-container *ngIf="senderLabel(teamChat?.contacts?.[0]?.fromUid) as asLabel">
        as {{ asLabel }}</ng-container>.
    </p>

    <mat-form-field appearance="outline" class="opener-field">
      <mat-label>Start from</mat-label>
      <mat-select [value]="selectedOpener" (valueChange)="applyOpener($event)">
        <mat-option *ngFor="let o of openers" [value]="o.key">{{ o.label }}</mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="message-field">
      <mat-label>Message</mat-label>
      <textarea
        matInput
        rows="5"
        [(ngModel)]="messageText"
        (ngModelChange)="onMessageEdited()"
        placeholder="They get a push notification, and it lands in their chats."
      ></textarea>
    </mat-form-field>

    <div class="composer-actions">
      <button
        mat-flat-button
        color="primary"
        (click)="sendTeamMessage()"
        [disabled]="sending || !messageText.trim() || !senderUid"
      >
        {{ sending ? 'Sending…' : 'Send as ' + (sender?.label ?? 'NX2U Team') }}
      </button>
      <span class="composer-meta">{{ messageText.length }} characters</span>
    </div>

    <p class="action-line error" *ngIf="teamChatError">{{ teamChatError }}</p>
    <p class="action-line notice" *ngIf="sentNotice">{{ sentNotice }}</p>
  </div>

  <!-- Sharon, the outreach persona: her open conversation with this member,
       or a way to start one on a cause. -->
  <app-sharon-panel
    *ngIf="sharonOpen"
    class="composer"
    [userId]="user.id"
    [firstName]="user.firstName"
    (closed)="sharonOpen = false"
  ></app-sharon-panel>

  <div class="content" *ngIf="!userProfileUrlForQRCode">
    <section class="card card--wide">
      <h3 class="card__title"><mat-icon aria-hidden="true">notes</mat-icon>About</h3>
      <p class="about" *ngIf="user.description?.trim(); else noAbout">{{ user.description }}</p>
      <ng-template #noAbout><p class="empty">No description yet.</p></ng-template>
    </section>

    <section class="card">
      <h3 class="card__title"><mat-icon aria-hidden="true">work_outline</mat-icon>Industries</h3>
      <div class="pills" *ngIf="user.industry?.length; else noIndustry">
        <span class="pill" *ngFor="let industry of user.industry">{{ industry }}</span>
      </div>
      <ng-template #noIndustry><p class="empty">None listed.</p></ng-template>
    </section>

    <section class="card">
      <h3 class="card__title"><mat-icon aria-hidden="true">handshake</mat-icon>Open to</h3>
      <div class="pills" *ngIf="openTo.length; else noOpenTo">
        <span class="pill pill--soft" *ngFor="let label of openTo">{{ label }}</span>
      </div>
      <ng-template #noOpenTo><p class="empty">Nothing picked.</p></ng-template>
    </section>

    <section class="card card--wide">
      <h3 class="card__title">
        <mat-icon aria-hidden="true">groups</mat-icon>Spaces
        <span class="card__count" *ngIf="user.events?.length">{{ user.events?.length }}</span>
      </h3>
      <div class="pills" *ngIf="user.events?.length; else noSpaces">
        <a
          *ngFor="let event of user.events"
          class="pill pill--link"
          [ngClass]="'pill--' + spaceRole(event)"
          [routerLink]="['/admin/spaces', event.id]"
          [matTooltip]="getSpaceChipTooltip(event)"
          mat-dialog-close
        >
          <mat-icon *ngIf="spaceRole(event) !== 'member'" aria-hidden="true">{{
            spaceRole(event) === 'owner' ? 'star' : 'shield'
          }}</mat-icon>
          {{ event.title }}
        </a>
      </div>
      <ng-template #noSpaces><p class="empty">Not in any space.</p></ng-template>
    </section>

    <section class="card">
      <h3 class="card__title"><mat-icon aria-hidden="true">info_outline</mat-icon>Details</h3>
      <dl class="details">
        <dt>Based in</dt>
        <dd [class.empty]="!user.basedIn">{{ user.basedIn || 'Not set' }}</dd>
        <dt>Travels</dt>
        <dd [class.empty]="!user.travelFrequency">{{ user.travelFrequency ? getTravelFrequencyText(user.travelFrequency) : 'Not set' }}</dd>
        <dt>Last seen</dt>
        <dd>{{ user.lastSeen ? getFromNow(user.lastSeen) : '—' }}</dd>
        <dt>Joined</dt>
        <dd>{{ user.createdAt ? getFromNow(user.createdAt) : '—' }}</dd>
        <ng-container *ngIf="user.email">
          <dt>Email</dt>
          <dd class="wrap">{{ user.email }}</dd>
        </ng-container>
      </dl>
    </section>

    <section class="card card--map">
      <h3 class="card__title">
        <mat-icon class="material-icons-outlined" aria-hidden="true">{{
          user.lastKnowLocation ? 'location_on' : 'location_off'
        }}</mat-icon>
        {{ user.lastKnowLocation ? 'Last known location' : 'No known location' }}
      </h3>
      <ng-container *ngIf="user.lastKnowLocation">
        <mat-progress-bar mode="indeterminate" *ngIf="!mapLoaded"></mat-progress-bar>
        <iframe
          class="map"
          width="100%"
          height="220"
          frameborder="0"
          referrerpolicy="no-referrer-when-downgrade"
          [src]="googleMapsIframeSrc"
          (load)="onIframeLoad($event)"
          allowfullscreen
        >
        </iframe>
      </ng-container>
      <p class="empty" *ngIf="!user.lastKnowLocation">The app has never reported where they are.</p>
    </section>
  </div>

  <div *ngIf="userProfileUrlForQRCode" class="qr-code">
    <qrcode
      [qrdata]="userProfileUrlForQRCode"
      [width]="500"
      [imageSrc]="user.profileImageUrl!"
      [imageHeight]="128"
      [imageWidth]="128"
      [errorCorrectionLevel]="'H'"
    />
  </div>
</div>
